import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "../../Styles/BlogDetails.css";
import {
  blog_Api,
  blogDetails_Api,
} from "../../Services/Operations/ProductAPI";
import { useSelector } from "react-redux";
import Footer from "../Components/Footer";

const BlogDetails = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const { configUrl } = useSelector((state) => state.cart);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const detailsResponse = await blogDetails_Api(slug);
        console.log("Blog details response:", detailsResponse);

        if (detailsResponse.success && detailsResponse.data) {
          const formattedBlog = {
            id: detailsResponse.data.id,
            title: detailsResponse.data.title,
            author: detailsResponse.data.author,
            date: new Date(
              detailsResponse.data.published_on
            ).toLocaleDateString("en-US", {
              day: "numeric",
              month: "short",
              year: "numeric",
            }),
            content: detailsResponse.data.long_desc,
            tags: detailsResponse.data.tags.split(",").map((tag) => tag.trim()),
            image: detailsResponse.data.image,
            slug: detailsResponse.data.slug,
          };
          setBlog(formattedBlog);

          // Now fetch all blogs to get related content
          const allBlogsResponse = await blog_Api();
          console.log("All blogs response:", allBlogsResponse);

          if (allBlogsResponse.success && allBlogsResponse.data) {
            // Format all blogs and filter out the current one
            const formattedAllBlogs = allBlogsResponse.data.map((blog) => ({
              id: blog.id,
              title: blog.title,
              author: blog.author,
              date: new Date(blog.published_on).toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
                year: "numeric",
              }),
              excerpt: blog.short_desc.replace(/<[^>]+>/g, ""),
              tags: blog.tags.split(",").map((tag) => tag.trim()),
              image: blog.image,
              slug: blog.slug,
            }));

            const related = formattedAllBlogs
              .filter((b) => b.id !== formattedBlog.id)
              .slice(0, 2);

            setRelatedBlogs(related);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="blog-details-container">
        <article className="blog-article-skeleton">
          <div className="skeleton-breadcrumb">
            <span className="skeleton-line short"></span>
          </div>

          <div className="skeleton-title"></div>

          <div className="skeleton-meta">
            <span className="skeleton-line short"></span>
            <span className="skeleton-line short"></span>
          </div>

          <div className="skeleton-tags">
            <span className="skeleton-tag"></span>
            <span className="skeleton-tag"></span>
            <span className="skeleton-tag"></span>
          </div>

          <div className="skeleton-image"></div>

          <div className="skeleton-content">
            <div className="skeleton-line long"></div>
            <div className="skeleton-line medium"></div>
            <div className="skeleton-line long"></div>
            <div className="skeleton-line short"></div>
            <div className="skeleton-line long"></div>
            <div className="skeleton-line medium"></div>
          </div>

          <div className="skeleton-share">
            <span className="skeleton-line short"></span>
            <div className="skeleton-share-buttons">
              <span className="skeleton-button"></span>
              <span className="skeleton-button"></span>
              <span className="skeleton-button"></span>
            </div>
          </div>
        </article>

        <aside className="related-blogs-skeleton">
          <div className="skeleton-title"></div>
          <div className="related-blogs-grid-skeleton">
            {[...Array(2)].map((_, index) => (
              <div key={index} className="related-blog-card-skeleton">
                <div className="skeleton-image small"></div>
                <div className="skeleton-content">
                  <div className="skeleton-line long"></div>
                  <div className="skeleton-meta">
                    <span className="skeleton-line short"></span>
                    <span className="skeleton-line short"></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    );
  }

  if (!blog) {
    return <div className="not-found">Blog post not found</div>;
  }

  return (
    <>
      <div className="blog-details-container">
        <article className="blog-article">
          <header className="article-header">
            <h1 className="article-title">{blog.title}</h1>
            <div className="article-meta">
              <span className="author">By {blog.author}</span>
              <span className="date">{blog.date}</span>
            </div>
            <div className="article-tags">
              {blog.tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="article-image-container">
            <img
              src={`${configUrl}${blog.image}`}
              alt={blog.title}
              className="article-image"
            />
          </div>

          <div
            className="article-content"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          ></div>

          <footer className="article-footer">
            <div className="share-buttons">
              <span>Share this article:</span>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                  window.location.href
                )}&text=${encodeURIComponent(blog.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-button twitter"
              >
                Twitter
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  window.location.href
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-button facebook"
              >
                Facebook
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                  window.location.href
                )}&title=${encodeURIComponent(blog.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-button linkedin"
              >
                LinkedIn
              </a>
            </div>
          </footer>
        </article>

        <aside className="related-blogs">
          <h3>Related Articles</h3>
          <div className="related-blogs-grid">
            {relatedBlogs.map((relatedBlog) => (
              <Link
                to={`/blog/${relatedBlog.slug}`}
                key={relatedBlog.id}
                className="related-blog-card"
              >
                <img
                  src={`${configUrl}${relatedBlog.image}`}
                  alt={relatedBlog.title}
                  className="related-blog-image"
                />
                <div className="related-blog-content">
                  <h4>{relatedBlog.title}</h4>
                  <p className="related-blog-meta">
                    <span>{relatedBlog.author}</span>
                    <span>{relatedBlog.date}</span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </aside>
      </div>
      <Footer />
    </>
  );
};

export default BlogDetails;
